import { useEffect, useRef, useState } from 'react';

import { ChatStreamEventCallbacks, applyChatSSEEvent } from '../helpers/chatStreamEvents.helpers';
import {
  clearPersistedJob,
  clearPersistedJobIfMatches,
  getPersistedJob,
} from '../helpers/jobRecovery.helpers';
import { CHAT_JOB_TYPE, ChatJobContext } from './useSSEStreaming';
import { useJobPolling } from './useJobPolling';

interface UseChatJobRecoveryParams {
  isV4Enabled: boolean;
  /** Built once, from the persisted context, the moment a resumable job is
   * found — lets the caller close over sessionType/sessionId/date/prompt
   * without needing them synchronously at render time. */
  getCallbacks: (context: ChatJobContext) => ChatStreamEventCallbacks;
  onDone: (context: ChatJobContext) => void;
  onError: (context: ChatJobContext) => void;
}

/**
 * Resumes a chat turn that was still generating when the tab reloaded or was
 * closed. Mounted once at app root (inside usePlanEntryHandler) — reads the
 * job_id persisted by useSSEStreaming's startStreaming, and if one exists,
 * polls GET /api/v1/jobs/{job_id} and replays events through the exact same
 * handlers (handleStreamingDelta, handleWholeMessage, handleFinalResult, …)
 * a live stream would have driven.
 */
export const useChatJobRecovery = ({
  isV4Enabled,
  getCallbacks,
  onDone,
  onError,
}: UseChatJobRecoveryParams): void => {
  const [resumeJobId, setResumeJobId] = useState<string | null>(null);
  const contextRef = useRef<ChatJobContext | null>(null);
  const callbacksRef = useRef<ChatStreamEventCallbacks | null>(null);

  useEffect(() => {
    const persisted = getPersistedJob<ChatJobContext>(CHAT_JOB_TYPE);
    if (!persisted) {
      return;
    }
    contextRef.current = persisted.context;
    callbacksRef.current = getCallbacks(persisted.context);
    setResumeJobId(persisted.jobId);
    // Mount-only: this hook has a single, fixed job to pick up per app load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useJobPolling({
    jobId: resumeJobId,
    onEvent: (event) => {
      if (!callbacksRef.current) return;
      applyChatSSEEvent(event.type, JSON.stringify(event.data), callbacksRef.current, isV4Enabled);
    },
    onDone: () => {
      if (contextRef.current) {
        onDone(contextRef.current);
      }
      if (resumeJobId) {
        clearPersistedJobIfMatches(CHAT_JOB_TYPE, resumeJobId);
      }
      setResumeJobId(null);
    },
    onError: () => {
      if (contextRef.current) {
        onError(contextRef.current);
      }
      clearPersistedJob(CHAT_JOB_TYPE);
      setResumeJobId(null);
    },
    onCancelled: () => {
      if (resumeJobId) {
        clearPersistedJobIfMatches(CHAT_JOB_TYPE, resumeJobId);
      }
      setResumeJobId(null);
    },
  });
};
